import React from 'react';
import { Package, Warning } from '@phosphor-icons/react';

import { Icon } from '../../../design-system/components/Icon';
import { Glyph } from '../../../design-system/components/shells/glyphs';
import { cn } from '../../../design-system/lib/cn';
import { useViewport } from '../../../design-system/hooks/useViewport';
import {
  Button as DskButton,
  Input as DskInput,
  Pill,
  Rating,
  Spinner,
  Textarea as DskTextarea,
} from '../../../design-system/components/desktop/primitives';
import { Modal, Stepper } from '../../../design-system/components/desktop/complex';
import {
  Cta,
  Input as DsInput,
  ProgressThread,
  Scale,
  Sheet,
  SheetBody,
  SheetHeader,
  StatusPill,
  Textarea as DsTextarea,
} from '../../../design-system/components/mobile';

import { TRACKING_STEPS, labelFor, mobileToneFor, toneFor } from './tracking.status';
import './tracking.css';

/**
 * The track-neutral parts of archetype A8, "lookup then status".
 *
 * WHY THESE ARE WRAPPERS AND NOT JUST CLASS NAMES
 * -----------------------------------------------
 * `design-system/README.md` section 5: the two tracks must never be mixed in
 * one rendered view, and where the DOM genuinely differs the choice is made in
 * JS with `useViewport()`, mounting ONE tree. A `.dsk-btn` and a `.ds-cta` are
 * not one button at two widths, and a `.dsk-modal` and a `.ds-sheet` are not
 * one dialog at two widths, so each of those gets a wrapper here that picks a
 * track and stays there. Nothing in this file renders both trees.
 *
 * The scaffolding around them, the surfaces, the fact lists, the action row,
 * is byte-identical on both tracks and only its metrics change, so it is a
 * media query in `tracking.css` under the track-neutral `dsa-` prefix, exactly
 * as `patterns/README.md` describes and as `pages/Sets/sets.css` already does.
 *
 * WHY THIS IS NOT IN `design-system/patterns/`
 * --------------------------------------------
 * `patterns/README.md` is explicit that a shape confined to one directory stays
 * with its page. This shape currently serves `/order-tracking` and
 * `/admin-order-tracking`, and both of those render the ONE view next door.
 * It becomes a promotion candidate the moment `/create-label` converts, which
 * `CONVERSION-MAP.md` records as the third A8 route.
 */

/* --------------------------------------------------------------- surfaces */

/** The page's own card surface. Borderless, tone-lift, no shadow. */
export function TrackCard({ className, ...rest }) {
  return <section className={cn('dsa-track__card', className)} {...rest} />;
}

/**
 * A titled panel. Desktop borrows `.dsk-panel` for the head hairline and the
 * glyph chip; below the handoff the same DOM drops to a plain titled block, so
 * no `dsk-` class ever reaches a mobile view.
 */
export function TrackPanel({ icon, title, sub, children, className, ...rest }) {
  const { isDesktop } = useViewport();
  return (
    <section className={cn('dsa-track__panel', isDesktop && 'dsk-panel', className)} {...rest}>
      <div className={cn('dsa-track__panel-head', isDesktop && 'dsk-panel__head')}>
        {isDesktop && icon ? (
          <span className="dsk-panel__icon">
            <Icon as={icon} />
          </span>
        ) : null}
        <div className={cn(isDesktop && 'dsk-panel__heading')}>
          <h3 className={cn('dsa-track__panel-title', isDesktop && 'dsk-panel__title')}>{title}</h3>
          {sub ? (
            <p className={cn('dsa-track__panel-sub', isDesktop && 'dsk-panel__sub')}>{sub}</p>
          ) : null}
        </div>
      </div>
      <div className={cn('dsa-track__panel-body', isDesktop && 'dsk-panel__body')}>{children}</div>
    </section>
  );
}

/** Two panels side by side above the handoff, stacked below it. */
export function TrackPanels({ className, ...rest }) {
  return <div className={cn('dsa-track__panels', className)} {...rest} />;
}

/* ----------------------------------------------------------------- facts */

/** A definition list of shipment facts, label left, value right. */
export function TrackFacts({ className, ...rest }) {
  return <dl className={cn('dsa-track__facts', className)} {...rest} />;
}

/**
 * One fact.
 *
 * `mono` is for an identifier, which gets the tabular figures and the extra
 * weight so a tracking number can be read back over a phone. Renders nothing
 * at all when there is no value: an empty row that says "-" is worse than an
 * absent row, and this payload legitimately omits most of these.
 */
export function TrackFact({ label, value, mono = false }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="dsa-track__fact">
      <dt>{label}</dt>
      <dd className={cn(mono && 'is-mono')}>{value}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ pill */

/**
 * The status pill. One tone table, four buckets plus `primary`, read out of
 * `utils/orderStatus.js` and never retyped.
 */
export function TrackStatusPill({ status, className }) {
  const { isDesktop } = useViewport();
  const label = labelFor(status);
  if (!label) return null;

  if (isDesktop) {
    return (
      <Pill tone={toneFor(status)} className={cn('dsk-pill--dot', className)}>
        {label}
      </Pill>
    );
  }
  return (
    <StatusPill tone={mobileToneFor(status)} className={className}>
      {label}
    </StatusPill>
  );
}

/* --------------------------------------------------------------- stepper */

/**
 * The four-node customer stepper: Collected, In transit, Out for delivery,
 * Delivered. Every one of those is a real `ORDER_STATUS_CONFIG` label.
 *
 * Desktop draws the library `Stepper`, dots and connectors with the labels
 * under them. Below the handoff there is no room for four labelled nodes on a
 * 360px screen, so the mobile track draws `ProgressThread`, the 4px bar, with
 * the current node named beside a "step N of 4" readout. Same information,
 * two DOMs, one of them mounted.
 *
 * `current` of -1 means nothing has been reached yet, which is honest for a
 * parcel that is still awaiting collection.
 */
export function TrackStepper({ current = -1 }) {
  const { isDesktop } = useViewport();
  const reached = Math.max(current, -1);
  const done = Math.min(reached + 1, TRACKING_STEPS.length);
  const name = reached < 0 ? 'Not collected yet' : TRACKING_STEPS[reached];
  const description =
    reached < 0
      ? `Not collected yet. Next step: ${TRACKING_STEPS[0]}.`
      : `Step ${done} of ${TRACKING_STEPS.length}: ${name}.`;

  if (isDesktop) {
    return (
      <div className="dsa-track__stepper">
        <Stepper steps={TRACKING_STEPS} current={reached} label={description} showLabels />
      </div>
    );
  }

  return (
    <div className="dsa-track__stepper">
      <ProgressThread
        percent={(done / TRACKING_STEPS.length) * 100}
        aria-label={description}
      />
      <div className="dsa-track__stepper-read">
        <span className="dsa-track__stepper-now">{name}</span>
        <span className="dsa-track__stepper-count">
          {done} of {TRACKING_STEPS.length}
        </span>
      </div>
    </div>
  );
}

/**
 * What replaces the stepper when the parcel is not going to arrive.
 *
 * A returned, cancelled or failed parcel is not partway along a line towards
 * delivery, so drawing a partial bar for it would be a lie. Border, glyph and
 * words, so the severity is never carried by colour on its own.
 */
export function TrackHalted({ children }) {
  return (
    <div className="dsa-track__halted" role="status">
      <Icon as={Warning} size="sm" />
      <span>{children}</span>
    </div>
  );
}

/* ------------------------------------------------------- lookup controls */

/** The tracking-ID text input, per track. */
export const TrackInput = React.forwardRef(function TrackInput({ invalid, ...rest }, ref) {
  const { isDesktop } = useViewport();
  if (isDesktop) {
    return <DskInput ref={ref} block error={invalid} className="dsa-track__id-input" {...rest} />;
  }
  return <DsInput ref={ref} big error={invalid} className="dsa-track__id-input" {...rest} />;
});

/** A textarea, per track. */
export const TrackTextarea = React.forwardRef(function TrackTextarea(props, ref) {
  const { isDesktop } = useViewport();
  if (isDesktop) return <DskTextarea ref={ref} {...props} />;
  return <DsTextarea ref={ref} {...props} />;
});

/**
 * A command, per track.
 *
 * Two variants and no more. `primary` is INK on both tracks: the pill already
 * carries the outcome and a button is a command regardless of it. There is no
 * `danger` variant, because `.dsk-btn--danger` does not exist; a destructive
 * command here is a ghost button plus a confirmation.
 */
export function TrackButton({ variant = 'primary', size, loading = false, children, ...rest }) {
  const { isDesktop } = useViewport();

  const body = loading ? (
    <>
      <Spinner size="sm" />
      <span>{children}</span>
    </>
  ) : (
    children
  );

  if (isDesktop) {
    return (
      <DskButton variant={variant} size={size} {...rest}>
        {body}
      </DskButton>
    );
  }
  return (
    <Cta variant={variant === 'primary' ? 'solid' : 'ghost'} size={size === 'sm' ? 'sm' : 'base'} {...rest}>
      {body}
    </Cta>
  );
}

/** The row a page's commands sit in. */
export function TrackActions({ note, children, className, ...rest }) {
  return (
    <div className={cn('dsa-track__actions', className)} {...rest}>
      {children}
      {note ? <span className="dsa-track__actions-note">{note}</span> : null}
    </div>
  );
}

/* ----------------------------------------------------------------- promise */

/**
 * The promise under the lookup form.
 *
 * Never a courier, never a city of origin, never a shipping price: none of
 * those are things we can pull, and the standing copy rule forbids all three.
 * "Nationwide" is the true and sufficient statement.
 */
export function TrackPromise() {
  return (
    <div className="dsa-track__promise">
      <p>Sleevr holds payment until the card is delivered.</p>
      <p>Every parcel ships nationwide, tracked from collection to your door.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ dialog */

/**
 * The form dialog, per track.
 *
 * `DESKTOP-PREFERENCES.md` section 5, "bottom sheet to modal for forms": these
 * are two different components with two different focus, scrim and scroll-lock
 * contracts, so exactly one of them mounts and neither is ever hidden with CSS.
 */
export function TrackDialog({ open, onClose, title, sub, children }) {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return (
      <Modal open={open} onClose={onClose} title={title}>
        {sub ? <p className="dsa-track__dialog-sub">{sub}</p> : null}
        {children}
      </Modal>
    );
  }

  /* `open` is passed through rather than gated by an early return: the Sheet
     stays mounted for its own exit, and unmounting it here would leave the
     slide-down with nothing to play on. `Modal` above already gates itself. */
  return (
    <Sheet open={Boolean(open)} onClose={onClose} aria-label={title}>
      <SheetHeader title={title} subtitle={sub} onClose={onClose} />
      <SheetBody>{children}</SheetBody>
    </Sheet>
  );
}

/* ---------------------------------------------------------------- rating */

/**
 * The 1 to 5 rating input, per track.
 *
 * Desktop is the library `Rating` in input mode: real radios, ink stars, the
 * fill and the keyboard all handled in CSS.
 *
 * The mobile track has no star INPUT. It has `StarGlyph` for reading a rating
 * back and `Scale`, the 1 to 5 tile row, for setting one, and `Scale` is the
 * right answer here rather than a hand-drawn star row: it is the track's own
 * component for exactly this choice, the tiles are 44px tap targets, and the
 * selected tile inverts to INK, which is the same commit doctrine the stars
 * follow. What it must NOT be is `dsk-rating` rendered below the handoff,
 * which is what this branch exists to prevent.
 *
 * The old `RateUs` went with the conversion: five hand-drawn SVGs, a hardcoded
 * `#fbbf24`, a `.dark-theme .star-empty` rule and no keyboard access at all.
 */
export function TrackRating({ value = 0, onChange = () => {}, label = 'Your rating' }) {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return (
      <Rating input name="review-rating" value={value} onChange={onChange} label={label} />
    );
  }

  return (
    <Scale
      min={1}
      max={5}
      value={value || undefined}
      onSelect={onChange}
      label={label}
      lowHint="Poor"
      highHint="Excellent"
      tileLabel={(point) => `${point} out of 5`}
    />
  );
}

/* -------------------------------------------------------------- dropzone */

/** The single-image evidence dropzone. `CustomImagePicker` took one file. */
export function TrackDropzone({ preview, onChange, id }) {
  const { isDesktop } = useViewport();
  const inputRef = React.useRef(null);

  return (
    <div className="dsa-track__dropzone">
      <button
        type="button"
        className="dsa-track__dropzone-btn"
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {preview ? (
          <img src={preview} alt="Selected evidence" className="dsa-track__dropzone-img" />
        ) : (
          <>
            {/* Phosphor above the handoff, the shells' 24 grid glyph below it.
                `Icon` emits `dsk-icon`, which must not reach a mobile view. */}
            {isDesktop ? (
              <Icon as={Package} size="xl" />
            ) : (
              <span className="dsa-track__dropzone-mark" aria-hidden="true">
                <Glyph name="plus" />
              </span>
            )}
            <strong>Add a photo</strong>
            <span>One image of the card as it arrived.</span>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        className="dsa-track__dropzone-input"
        onChange={onChange}
      />
    </div>
  );
}
